import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { 
    AlertCircle, Activity, FileText, UserPlus, FlaskConical, Thermometer, Stethoscope,
    Plus, Search, Edit2, Trash2, ChevronDown, ChevronUp, GripVertical
} from 'lucide-react';

// --- КОНФИГУРАЦИЯ ВСЕХ 7 ВКЛАДОК ---
const TABS_CONFIG = {
    allergies: {
        id: 'allergies', label: 'Аллергии', icon: <AlertCircle size={18}/>,
        fields: [
            { name: 'name', label: 'Название аллергена', type: 'text', req: true },
            { name: 'type', label: 'Тип', type: 'select', options: ['Медикаменты', 'Пища', 'Респираторные', 'Насекомые', 'Контактные', 'Физические'], req: true },
            { name: 'severity', label: 'Степень тяжести', type: 'select', options: ['Легкая', 'Средняя', 'Тяжелая', 'Анафилаксия'], req: true },
            { name: 'desc', label: 'Описание', type: 'textarea', full: true }
        ],
        columns: [
            { key: 'name', label: 'Название аллергена' },
            { key: 'type', label: 'Тип', badge: true },
            { key: 'severity', label: 'Степень тяжести' }
        ]
    },
    chronic: {
        id: 'chronic', label: 'Хронические заболевания', icon: <Activity size={18}/>,
        fields: [
            { name: 'name', label: 'Название', type: 'text', req: true },
            { name: 'icd', label: 'МКБ-10 код', type: 'text', req: true },
            { name: 'cat', label: 'Категория', type: 'text', req: true },
            { name: 'desc', label: 'Описание', type: 'textarea', full: true }
        ],
        columns: [ { key: 'name', label: 'Название' }, { key: 'icd', label: 'МКБ-10 код', bold: true }, { key: 'cat', label: 'Категория' } ]
    },
    diagnoses: {
        id: 'diagnoses', label: 'Диагнозы', icon: <FileText size={18}/>,
        fields: [
            { name: 'name', label: 'Название', type: 'text', req: true },
            { name: 'icd', label: 'МКБ-10 код', type: 'text', req: true },
            { name: 'cat', label: 'Категория', type: 'text', req: true },
            { name: 'symp', label: 'Типичные симптомы', type: 'text', full: true },
            { name: 'desc', label: 'Описание', type: 'textarea', full: true }
        ],
        columns: [ { key: 'name', label: 'Название' }, { key: 'icd', label: 'МКБ-10 код', bold: true }, { key: 'cat', label: 'Категория' } ]
    },
    specialties: {
        id: 'specialties', label: 'Специальности врачей', icon: <UserPlus size={18}/>,
        fields: [
            { name: 'nameru', label: 'Название на русском', type: 'text', req: true },
            { name: 'nametj', label: 'Название на таджикском', type: 'text', req: true },
            { name: 'code', label: 'Код специальности', type: 'text', req: true },
            { name: 'dept', label: 'Отделение', type: 'text', req: true },
            { name: 'desc', label: 'Описание', type: 'textarea', full: true }
        ],
        columns: [ { key: 'nameru', label: 'На русском' }, { key: 'nametj', label: 'На таджикском' }, { key: 'code', label: 'Код' }, { key: 'dept', label: 'Отделение' } ]
    },
    lab_tests: {
        id: 'lab_tests', label: 'Лабораторные анализы', icon: <FlaskConical size={18}/>,
        fields: [], columns: []
    },
    symptoms: {
        id: 'symptoms', label: 'Симптомы', icon: <Thermometer size={18}/>,
        fields: [
            { name: 'name', label: 'Название', type: 'text', req: true },
            { name: 'cat', label: 'Категория', type: 'text', req: true },
            { name: 'rel', label: 'Связанные заболевания', type: 'textarea', full: true }
        ],
        columns: [ { key: 'name', label: 'Название' }, { key: 'cat', label: 'Категория', badge: true }, { key: 'rel', label: 'Связанные заболевания' } ]
    },
    procedures: {
        id: 'procedures', label: 'Процедуры', icon: <Stethoscope size={18}/>,
        fields: [
            { name: 'name', label: 'Название', type: 'text', req: true },
            { name: 'type', label: 'Тип', type: 'text', req: true },
            { name: 'cat', label: 'Категория', type: 'text', req: true },
            { name: 'duration', label: 'Длительность', type: 'text', req: true },
            { name: 'price', label: 'Стоимость (TJS)', type: 'number', req: true },
            { name: 'desc', label: 'Описание', type: 'textarea', full: true }
        ],
        columns: [ { key: 'name', label: 'Название' }, { key: 'type', label: 'Тип', badge: true }, { key: 'cat', label: 'Категория' }, { key: 'duration', label: 'Длительность' }, { key: 'price', label: 'Стоимость', suffix: ' TJS' } ]
    }
};

const MedicalBooks = () => {
    const [activeTab, setActiveTab] = useState('allergies');
    const [records, setRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Состояния формы
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({});
    const [editingId, setEditingId] = useState(null);

    // Состояния для раскрывающихся списков и Drag-and-Drop
    const [expandedLabs, setExpandedLabs] = useState({});
    const [dragRecordIndex, setDragRecordIndex] = useState(null);
    const [dragParamIndex, setDragParamIndex] = useState(null);

    const conf = TABS_CONFIG[activeTab];

    useEffect(() => {
        fetchData(activeTab);
        setShowForm(false);
        setSearchQuery('');
        setExpandedLabs({});
    }, [activeTab]);

    const fetchData = async (tab) => {
        setIsLoading(true);
        try {
            const res = await api.get(`/dict/${tab}`);
            const parsed = res.data.map(item => ({ id: item.id, ...JSON.parse(item.data) }));
            // Сортировка по полю order (если оно есть)
            parsed.sort((a, b) => (a.order || 0) - (b.order || 0));
            setRecords(parsed);
        } catch (e) { console.error(e); } finally { setIsLoading(false); }
    };

    const handleAddNew = () => {
        setEditingId(null);
        if (activeTab === 'lab_tests') {
            setFormData({ name: '', cat: '', time: '', desc: '', parameters: [] });
        } else {
            let emptyForm = {};
            conf.fields.forEach(f => emptyForm[f.name] = '');
            setFormData(emptyForm);
        }
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        const itemData = { ...item };
        delete itemData.id; 
        
        if (activeTab === 'lab_tests' && !itemData.parameters) {
            itemData.parameters = [];
        }
        
        setFormData(itemData);
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const itemData = { ...formData };
            // Присваиваем индекс сортировки новым анализам
            if (!editingId && activeTab === 'lab_tests') {
                itemData.order = records.length;
            }

            const payload = { data: JSON.stringify(itemData) };
            if (editingId) {
                await api.put(`/dict/${activeTab}/${editingId}`, payload);
            } else {
                await api.post(`/dict/${activeTab}`, payload);
            }
            setShowForm(false);
            fetchData(activeTab);
        } catch (e) { alert('Ошибка сохранения'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить запись?')) return;
        try {
            await api.delete(`/dict/${activeTab}/${id}`);
            setRecords(records.filter(r => r.id !== id));
        } catch (e) { alert('Ошибка удаления'); }
    };

    const toggleLabExpand = (id) => {
        setExpandedLabs(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // --- ЛОГИКА DRAG AND DROP ДЛЯ АНАЛИЗОВ (КАРТОЧЕК) ---
    const onRecordDragStart = (e, index) => {
        if (searchQuery) {
            e.preventDefault(); // Запрещаем сортировку при активном поиске
            return;
        }
        setDragRecordIndex(index);
    };

    const onRecordDragEnter = (index) => {
        if (dragRecordIndex === null || dragRecordIndex === index) return;
        const newRecords = [...records];
        const dragged = newRecords[dragRecordIndex];
        newRecords.splice(dragRecordIndex, 1);
        newRecords.splice(index, 0, dragged);
        setDragRecordIndex(index);
        setRecords(newRecords);
    };

    const onRecordDragEnd = async () => {
        setDragRecordIndex(null);
        if (searchQuery) return;
        
        // Автоматическое сохранение нового порядка в базу данных
        try {
            const promises = records.map((rec, idx) => {
                const itemData = { ...rec };
                delete itemData.id;
                itemData.order = idx; // Обновляем индекс
                return api.put(`/dict/${activeTab}/${rec.id}`, { data: JSON.stringify(itemData) });
            });
            await Promise.all(promises);
        } catch (e) {
            console.error('Ошибка сохранения сортировки');
        }
    };

    // --- ЛОГИКА DRAG AND DROP ДЛЯ ПАРАМЕТРОВ (ВНУТРИ ФОРМЫ) ---
    const addParameter = () => {
        const newParams = [...(formData.parameters || []), { name: '', unit: '', norm: '' }];
        setFormData({ ...formData, parameters: newParams });
    };

    const updateParameter = (index, field, value) => {
        const newParams = [...formData.parameters];
        newParams[index][field] = value;
        setFormData({ ...formData, parameters: newParams });
    };

    const removeParameter = (index) => {
        const newParams = [...formData.parameters];
        newParams.splice(index, 1);
        setFormData({ ...formData, parameters: newParams });
    };

    const onParamDragStart = (index) => setDragParamIndex(index);
    
    const onParamDragEnter = (index) => {
        if (dragParamIndex === null || dragParamIndex === index) return;
        const newParams = [...formData.parameters];
        const dragged = newParams[dragParamIndex];
        newParams.splice(dragParamIndex, 1);
        newParams.splice(index, 0, dragged);
        setDragParamIndex(index);
        setFormData({ ...formData, parameters: newParams });
    };

    const onParamDragEnd = () => setDragParamIndex(null);

    // Поиск
    const filteredRecords = records.filter(item => {
        const str = Object.values(item).map(val => typeof val === 'string' ? val : JSON.stringify(val)).join(' ').toLowerCase();
        return str.includes(searchQuery.toLowerCase());
    });

    const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.9rem', color: '#111827' };
    const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '6px' };

    return (
        <div className="content-wrapper medical-books-page">
            <div className="page-header" style={{borderBottom: '1px solid #cbd5e1', paddingBottom: '1.5rem', marginBottom: '0'}}>
                <h1 style={{fontSize: '1.5rem', fontWeight: 'bold', margin:0}}>Медицинские справочники</h1>
                <p style={{fontSize: '.875rem', color: '#475569', margin: '0.25rem 0 0 0'}}>Управление базой данных системы eDukhtur</p>
            </div>

            <div className="mb-tabs-nav">
                {Object.values(TABS_CONFIG).map(tab => (
                    <button 
                        key={tab.id} 
                        className={`mb-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            <div className="card" style={{padding: activeTab === 'lab_tests' && !showForm ? '0' : '1.5rem', background: activeTab === 'lab_tests' && !showForm ? 'transparent' : '#fff', border: activeTab === 'lab_tests' && !showForm ? 'none' : '1px solid #e5e7eb', boxShadow: activeTab === 'lab_tests' && !showForm ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'}}>
                
                <div className="mb-action-bar" style={{ background: activeTab === 'lab_tests' && !showForm ? '#fff' : 'transparent', padding: activeTab === 'lab_tests' && !showForm ? '16px' : '0', borderRadius: '12px' }}>
                    <div>
                        <h2 style={{fontSize: '1.2rem', fontWeight: 'bold', margin: '0 0 4px 0'}}>{conf.label}</h2>
                        <p style={{fontSize: '0.85rem', color: '#64748b', margin: 0}}>Всего записей: {records.length}</p>
                    </div>
                    <button className="btn btn-primary" onClick={handleAddNew} style={{background: '#2563eb', color: '#fff', padding: '10px 20px', borderRadius: '8px', border: 'none', display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', fontWeight: '500'}}>
                        <Plus size={18}/> Добавить {activeTab === 'lab_tests' ? 'анализ' : ''}
                    </button>
                </div>

                {!showForm && (
                    <div style={{position: 'relative', marginBottom: activeTab === 'lab_tests' ? '16px' : '0'}}>
                        <Search size={18} style={{position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}}/>
                        <input 
                            type="text" 
                            style={{width: '100%', padding: '12px 16px 12px 42px', borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '0.95rem', outline: 'none', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'}} 
                            placeholder={`Поиск по разделу "${conf.label}"...`}
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                )}

                {showForm && activeTab !== 'lab_tests' && (
                    <div className="mb-form-panel">
                        <h3 style={{marginBottom: '1rem', fontSize: '1.1rem'}}>{editingId ? 'Редактировать запись' : 'Добавить новую запись'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="mb-form-grid">
                                {conf.fields.map(field => (
                                    <div key={field.name} className={`mb-form-group ${field.full ? 'full' : ''}`}>
                                        <label className="mb-label">{field.label} {field.req && <span>*</span>}</label>
                                        
                                        {field.type === 'textarea' ? (
                                            <textarea className="mb-input" rows="2" value={formData[field.name] || ''} onChange={e => setFormData({...formData, [field.name]: e.target.value})} required={field.req} />
                                        ) : field.type === 'select' ? (
                                            <select className="mb-input" value={formData[field.name] || ''} onChange={e => setFormData({...formData, [field.name]: e.target.value})} required={field.req}>
                                                <option value="">Выберите...</option>
                                                {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                        ) : (
                                            <input type={field.type} className="mb-input" value={formData[field.name] || ''} onChange={e => setFormData({...formData, [field.name]: e.target.value})} required={field.req} />
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div style={{display: 'flex', gap: '10px', marginTop: '1.5rem'}}>
                                <button type="submit" style={{background: '#16a34a', color: '#fff', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer'}}>Сохранить</button>
                                <button type="button" onClick={() => setShowForm(false)} style={{background: '#e2e8f0', color: '#475569', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer'}}>Отмена</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* --- КАСТОМНАЯ ФОРМА ДЛЯ ЛАБОРАТОРНЫХ АНАЛИЗОВ --- */}
                {showForm && activeTab === 'lab_tests' && (
                    <div style={{background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '30px'}}>
                        <h3 style={{fontSize: '1.4rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '24px', marginTop: '0'}}>
                            {editingId ? 'Редактировать анализ' : 'Новый анализ'}
                        </h3>
                        
                        <form onSubmit={handleSubmit}>
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '20px'}}>
                                <div>
                                    <label style={labelStyle}>Название анализа <span style={{color:'red'}}>*</span></label>
                                    <input style={inputStyle} value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Общий анализ крови (ОАК)" required />
                                </div>
                                <div>
                                    <label style={labelStyle}>Категория <span style={{color:'red'}}>*</span></label>
                                    <input style={inputStyle} value={formData.cat || ''} onChange={e => setFormData({...formData, cat: e.target.value})} placeholder="Гематология" required />
                                </div>
                                <div>
                                    <label style={labelStyle}>Срок готовности</label>
                                    <input style={inputStyle} value={formData.time || ''} onChange={e => setFormData({...formData, time: e.target.value})} placeholder="1-2 дня" />
                                </div>
                            </div>
                            
                            <div style={{marginBottom: '30px'}}>
                                <label style={labelStyle}>Описание</label>
                                <textarea style={{...inputStyle, minHeight: '60px', resize: 'vertical'}} value={formData.desc || ''} onChange={e => setFormData({...formData, desc: e.target.value})} placeholder="Исследование качественного и количественного состава..." />
                            </div>

                            <div style={{background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '30px'}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                        <h4 style={{fontSize: '1.1rem', fontWeight: 'bold', margin: 0}}>Параметры анализа</h4>
                                        <span style={{background: '#e2e8f0', color: '#475569', fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', fontWeight: '600'}}>
                                            {formData.parameters?.length || 0} парам.
                                        </span>
                                    </div>
                                    <button type="button" onClick={addParameter} style={{background: '#16a34a', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: '500'}}>
                                        <Plus size={16}/> Добавить параметр
                                    </button>
                                </div>

                                {(!formData.parameters || formData.parameters.length === 0) ? (
                                    <p style={{color: '#64748b', fontSize: '0.9rem', textAlign: 'center', padding: '20px 0'}}>Нет добавленных параметров.</p>
                                ) : (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                        <div style={{display: 'grid', gridTemplateColumns: '24px 2fr 1fr 2fr 40px', gap: '12px', padding: '0 10px', color: '#64748b', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                            <div></div>
                                            <div>Показатель</div>
                                            <div>Единицы</div>
                                            <div>Референс / Норма</div>
                                            <div></div>
                                        </div>
                                        
                                        {formData.parameters.map((param, index) => (
                                            <div 
                                                key={index} 
                                                draggable
                                                onDragStart={() => onParamDragStart(index)}
                                                onDragEnter={() => onParamDragEnter(index)}
                                                onDragEnd={onParamDragEnd}
                                                onDragOver={(e) => e.preventDefault()}
                                                style={{
                                                    display: 'grid', gridTemplateColumns: '24px 2fr 1fr 2fr 40px', gap: '12px', alignItems: 'center',
                                                    background: dragParamIndex === index ? '#f1f5f9' : 'transparent',
                                                    opacity: dragParamIndex === index ? 0.5 : 1,
                                                    transition: 'all 0.2s', padding: '4px 0', borderRadius: '8px'
                                                }}
                                            >
                                                <div style={{color: '#cbd5e1', cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                    <GripVertical size={18}/>
                                                </div>
                                                <input style={inputStyle} value={param.name} onChange={e => updateParameter(index, 'name', e.target.value)} placeholder="Гемоглобин" required />
                                                <input style={inputStyle} value={param.unit} onChange={e => updateParameter(index, 'unit', e.target.value)} placeholder="г/л" />
                                                <input style={inputStyle} value={param.norm} onChange={e => updateParameter(index, 'norm', e.target.value)} placeholder="М: 130-160, Ж: 120-140" />
                                                <button type="button" onClick={() => removeParameter(index)} style={{background: '#fee2e2', color: '#dc2626', border: 'none', height: '40px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                    <Trash2 size={16}/>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={{display: 'flex', gap: '12px'}}>
                                <button type="submit" style={{background: '#16a34a', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '1rem', fontWeight: '500', cursor: 'pointer'}}>Сохранить анализ</button>
                                <button type="button" onClick={() => setShowForm(false)} style={{background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '1rem', fontWeight: '500', cursor: 'pointer'}}>Отмена</button>
                            </div>
                        </form>
                    </div>
                )}

                {/* --- СТАНДАРТНАЯ ТАБЛИЦА (ДЛЯ ВСЕХ КРОМЕ АНАЛИЗОВ) --- */}
                {!showForm && activeTab !== 'lab_tests' && (
                    <div className="mb-table-container">
                        <table className="mb-table">
                            <thead>
                                <tr>
                                    {conf.columns.map(col => <th key={col.key}>{col.label}</th>)}
                                    <th style={{width: '100px'}}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? <tr><td colSpan={conf.columns.length + 1} style={{textAlign:'center'}}>Загрузка...</td></tr> :
                                filteredRecords.length === 0 ? <tr><td colSpan={conf.columns.length + 1} style={{textAlign:'center', color: '#94a3b8'}}>Нет данных</td></tr> :
                                filteredRecords.map(item => (
                                    <tr key={item.id}>
                                        {conf.columns.map(col => (
                                            <td key={col.key}>
                                                {col.badge ? <span className="mb-badge">{item[col.key]}</span> : 
                                                col.bold ? <strong>{item[col.key]}</strong> : 
                                                <span>{item[col.key]}{col.suffix || ''}</span>}
                                            </td>
                                        ))}
                                        <td>
                                            <div style={{display: 'flex', gap: '5px'}}>
                                                <button onClick={() => handleEdit(item)} style={{background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: '5px'}}><Edit2 size={16}/></button>
                                                <button onClick={() => handleDelete(item.id)} style={{background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '5px'}}><Trash2 size={16}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* --- КАСТОМНЫЙ СПИСОК АНАЛИЗОВ С РАСКРЫТИЕМ И DRAG-AND-DROP --- */}
                {!showForm && activeTab === 'lab_tests' && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                        {isLoading ? <p style={{textAlign:'center', color:'#64748b'}}>Загрузка...</p> : 
                         filteredRecords.length === 0 ? <p style={{textAlign:'center', color:'#64748b'}}>Анализы не найдены</p> : 
                         filteredRecords.map((item, index) => {
                            const isExpanded = !!expandedLabs[item.id];
                            const paramsCount = item.parameters ? item.parameters.length : 0;
                            
                            return (
                                <div 
                                    key={item.id} 
                                    draggable={!searchQuery} // Отключаем drag при поиске
                                    onDragStart={(e) => onRecordDragStart(e, index)}
                                    onDragEnter={() => onRecordDragEnter(index)}
                                    onDragEnd={onRecordDragEnd}
                                    onDragOver={(e) => e.preventDefault()}
                                    style={{
                                        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', 
                                        transition: 'all 0.2s', boxShadow: isExpanded ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none',
                                        opacity: dragRecordIndex === index ? 0.4 : 1
                                    }}
                                >
                                    {/* Шапка карточки */}
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px'}}>
                                        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                                            {!searchQuery && (
                                                <div style={{color: '#cbd5e1', cursor: 'grab', display: 'flex', alignItems: 'center'}}>
                                                    <GripVertical size={20}/>
                                                </div>
                                            )}
                                            <div style={{width: '40px', height: '40px', borderRadius: '8px', background: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                <FlaskConical size={20}/>
                                            </div>
                                            <div onClick={() => toggleLabExpand(item.id)} style={{cursor: 'pointer'}}>
                                                <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px'}}>
                                                    <h4 style={{margin: 0, fontSize: '1.1rem', fontWeight: 'bold', color: '#0f172a'}}>{item.name}</h4>
                                                    <span style={{background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600'}}>
                                                        {paramsCount} параметров
                                                    </span>
                                                </div>
                                                <p style={{margin: 0, fontSize: '0.85rem', color: '#64748b'}}>
                                                    {item.cat || 'Без категории'} • Готовность: {item.time || '-'}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                            <button onClick={(e) => { e.stopPropagation(); handleEdit(item); }} style={{background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                <Edit2 size={16}/>
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} style={{background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                <Trash2 size={16}/>
                                            </button>
                                            <div style={{width: '1px', height: '24px', background: '#e2e8f0', margin: '0 4px'}}></div>
                                            <div onClick={() => toggleLabExpand(item.id)} style={{color: '#94a3b8', display: 'flex', cursor: 'pointer'}}>
                                                {isExpanded ? <ChevronUp size={24}/> : <ChevronDown size={24}/>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Раскрывающаяся таблица */}
                                    {isExpanded && (
                                        <div style={{padding: '0 20px 20px 20px', borderTop: '1px solid #f1f5f9', background: '#fafaf9'}}>
                                            {item.desc && <p style={{fontSize: '0.85rem', color: '#64748b', margin: '16px 0'}}>{item.desc}</p>}
                                            
                                            {paramsCount === 0 ? (
                                                <p style={{fontSize: '0.85rem', color: '#94a3b8'}}>Параметры не добавлены.</p>
                                            ) : (
                                                <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '0.9rem'}}>
                                                    <thead>
                                                        <tr style={{borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                                            <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 'bold'}}>Показатель</th>
                                                            <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 'bold'}}>Единицы измерения</th>
                                                            <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 'bold'}}>Референс / Норма</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {item.parameters.map((p, idx) => (
                                                            <tr key={idx} style={{borderBottom: '1px solid #f1f5f9'}}>
                                                                <td style={{padding: '12px 8px', fontWeight: '600', color: '#334155'}}>{p.name}</td>
                                                                <td style={{padding: '12px 8px', color: '#64748b'}}>{p.unit || '-'}</td>
                                                                <td style={{padding: '12px 8px', color: '#64748b'}}>{p.norm || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                         })}
                    </div>
                )}

            </div>
        </div>
    );
};

export default MedicalBooks;